import {
  BUSINESS_UNIT_LABELS,
  type CustomerSubscription,
  POS_LINE_KIND_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUSES,
  type SubscriptionPlan,
  subscriptionStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarSync, Plus, ReceiptText } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import {
  entitlementPercent,
  formatEntitlement,
  ownerText,
  subscriptionOwner,
} from "../../../../features/loyalty/loyalty-values.js";
import {
  useLoyaltyPermissions,
  useLoyaltyProducts,
  useSubscriptionPlans,
  useSubscriptions,
} from "../../../../features/loyalty/queries.js";
import {
  ActiveBadge,
  LoyaltyCompanyPage,
  OwnerCell,
  SubscriptionStatusBadge,
} from "../../../../features/loyalty/shared.js";
import {
  BillingRunDialog,
  PlanDialog,
  SubscriptionCreateDialog,
} from "../../../../features/loyalty/subscription-dialogs.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDate } from "../../../../lib/format.js";

const TABS = ["subscriptions", "plans"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  status: subscriptionStatusSchema.optional().catch(undefined),
  plan: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/sadakat/abonelikler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SubscriptionsRoute,
});

/**
 * Abonelikler (ADR 0017 §27): şirketin müşteri abonelikleri (durum / plan süzgeci, bugünkü kalan hak) ve planları.
 * Web'den açılış: filo carisi / carili müşteri → faturalı; kasa aboneliği yalnızca gerekçeli ücretsiz tanım. Faturalı
 * aboneliklerin dönem ücreti "Faturalama turu" ile kesilir (`loyalty.subscription.manage` + `invoices.sales.manage`).
 */
function SubscriptionsRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useLoyaltyPermissions();
  const tab: TabKey = search.tab ?? "subscriptions";
  const [dialog, setDialog] = useState<"plan" | "subscription" | "billing" | null>(null);
  return (
    <LoyaltyCompanyPage
      title={t("menu.loyaltySubscriptions")}
      description={t("loyalty.subscriptions.description")}
      actions={() => (
        <>
          {permissions.canRunBilling ? (
            <Button variant="outline" onClick={() => setDialog("billing")}>
              <ReceiptText aria-hidden="true" />
              {t("loyalty.billing.open")}
            </Button>
          ) : null}
          {permissions.canManageSubscriptions ? (
            tab === "plans" ? (
              <Button onClick={() => setDialog("plan")}>
                <Plus aria-hidden="true" />
                {t("loyalty.plans.create")}
              </Button>
            ) : (
              <Button onClick={() => setDialog("subscription")}>
                <Plus aria-hidden="true" />
                {t("loyalty.subscriptions.create")}
              </Button>
            )
          ) : null}
        </>
      )}
    >
      {({ companyId }) => (
        <SubscriptionsBody
          companyId={companyId}
          tab={tab}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: ((previous: Record<string, unknown>) =>
                compactSearch({ ...previous, tab: next === "subscriptions" ? undefined : next })) as never,
              replace: true,
            })
          }
          dialog={dialog}
          onDialog={setDialog}
        />
      )}
    </LoyaltyCompanyPage>
  );
}

function SubscriptionsBody({
  companyId,
  tab,
  onTab,
  dialog,
  onDialog,
}: {
  companyId: string;
  tab: TabKey;
  onTab: (tab: TabKey) => void;
  dialog: "plan" | "subscription" | "billing" | null;
  onDialog: (dialog: "plan" | "subscription" | "billing" | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plans = useSubscriptionPlans(companyId, {});
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  return (
    <>
      <Tabs value={tab} onValueChange={(next) => onTab(next as TabKey)}>
        <TabsList aria-label={t("loyalty.subscriptions.tabsLabel")}>
          <TabsTrigger value="subscriptions">{t("loyalty.subscriptions.tabs.subscriptions")}</TabsTrigger>
          <TabsTrigger value="plans">{t("loyalty.subscriptions.tabs.plans")}</TabsTrigger>
        </TabsList>
        <TabsContent value="subscriptions">
          {tab === "subscriptions" ? (
            <SubscriptionList companyId={companyId} plans={plans.data?.items ?? []} />
          ) : null}
        </TabsContent>
        <TabsContent value="plans">
          {tab === "plans" ? (
            <PlanList
              plans={plans.data?.items ?? []}
              loading={plans.isPending}
              error={plans.error}
              onEdit={(plan) => {
                setEditingPlan(plan);
                onDialog("plan");
              }}
            />
          ) : null}
        </TabsContent>
      </Tabs>
      <PlanDialog
        open={dialog === "plan"}
        onOpenChange={(next) => {
          if (!next) setEditingPlan(null);
          onDialog(next ? "plan" : null);
        }}
        companyId={companyId}
        plan={editingPlan}
      />
      <SubscriptionCreateDialog
        open={dialog === "subscription"}
        onOpenChange={(next) => onDialog(next ? "subscription" : null)}
        plans={plans.data?.items ?? []}
        onCreated={(created) => void navigate({ to: "/sadakat/abonelikler/$id", params: { id: created.id } })}
      />
      <BillingRunDialog
        open={dialog === "billing"}
        onOpenChange={(next) => onDialog(next ? "billing" : null)}
        companyId={companyId}
      />
    </>
  );
}

function SubscriptionList({
  companyId,
  plans,
}: {
  companyId: string;
  plans: readonly SubscriptionPlan[];
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const pages = useCursorPages(`${companyId}|${search.status ?? ""}|${search.plan ?? ""}`);
  const list = useSubscriptions({ companyId, status: search.status, planId: search.plan }, pages.cursor);
  const setFilter = (key: "status" | "plan", value: string) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, [key]: value })) as never,
      replace: true,
    });

  const columns = useMemo<DataTableColumn<CustomerSubscription>[]>(
    () => [
      {
        id: "plan",
        header: t("loyalty.subscriptions.plan"),
        hideable: false,
        exportValue: (row) => row.planCode,
        cell: (row) => (
          <Link
            to="/sadakat/abonelikler/$id"
            params={{ id: row.id }}
            className="grid underline underline-offset-4"
          >
            <span>{row.planName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.planCode}</span>
          </Link>
        ),
      },
      {
        id: "subscriber",
        header: t("loyalty.subscriptions.subscriber"),
        exportValue: (row) => ownerText(subscriptionOwner(row)),
        cell: (row) => <OwnerCell owner={subscriptionOwner(row)} showKind />,
      },
      {
        id: "billing",
        header: t("loyalty.subscriptions.billing"),
        exportValue: (row) => row.billing,
        cell: (row) => (
          <span className="whitespace-nowrap">
            {row.billing === "INVOICE"
              ? t("loyalty.subscriptions.invoice")
              : t("loyalty.subscriptions.posBilling")}
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => SUBSCRIPTION_STATUS_LABELS[row.status],
        cell: (row) => <SubscriptionStatusBadge status={row.status} />,
      },
      {
        id: "period",
        header: t("loyalty.subscriptions.period"),
        exportValue: (row) => `${row.currentPeriodStart} – ${row.currentPeriodEnd}`,
        cell: (row) => (
          <span className="tabular whitespace-nowrap">
            {formatDate(row.currentPeriodStart)} – {formatDate(row.currentPeriodEnd)}
          </span>
        ),
      },
      {
        id: "remaining",
        header: t("loyalty.subscriptions.remaining"),
        align: "right",
        exportValue: (row) => row.usage.remaining,
        cell: (row) => {
          const percent = entitlementPercent(row.usage.used, row.usage.quantityPerPeriod);
          return (
            <span className="grid justify-items-end whitespace-nowrap">
              <span className="tabular">
                {t("loyalty.subscriptions.remainingText", {
                  remaining: formatEntitlement(row.usage.remaining),
                  total: formatEntitlement(row.usage.quantityPerPeriod),
                })}
              </span>
              {percent !== null ? (
                <span className="text-xs text-muted-foreground">
                  {t("loyalty.subscriptions.usedPercent", { percent })}
                </span>
              ) : null}
            </span>
          );
        },
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("loyalty.subscriptions.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<CalendarSync />}
          title={t("loyalty.subscriptions.emptyTitle")}
          description={t("loyalty.subscriptions.emptyDescription")}
        />
      }
      filters={
        <>
          <FormField label={t("common.status")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) => setFilter("status", event.target.value)}
              >
                <option value="">{t("common.all")}</option>
                {SUBSCRIPTION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {SUBSCRIPTION_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("loyalty.subscriptions.plan")} className="w-64">
            {(control) => (
              <Select
                {...control}
                value={search.plan ?? ""}
                onChange={(event) => setFilter("plan", event.target.value)}
              >
                <option value="">{t("common.all")}</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.code} {plan.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}

function PlanList({
  plans,
  loading,
  error,
  onEdit,
}: {
  plans: readonly SubscriptionPlan[];
  loading: boolean;
  error: unknown;
  onEdit: (plan: SubscriptionPlan) => void;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const products = useLoyaltyProducts();
  const productNames = useMemo(
    () => new Map((products.data ?? []).map((product) => [product.id, `${product.code} ${product.name}`])),
    [products.data],
  );

  const columns = useMemo<DataTableColumn<SubscriptionPlan>[]>(
    () => [
      {
        id: "code",
        header: t("loyalty.plans.fields.code"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => <span className="font-mono">{row.code}</span>,
      },
      {
        id: "name",
        header: t("loyalty.plans.fields.name"),
        exportValue: (row) => row.name,
        cell: (row) => (
          <span className="grid">
            <span>{row.name}</span>
            <span className="text-xs text-muted-foreground">{BUSINESS_UNIT_LABELS[row.businessUnit]}</span>
          </span>
        ),
      },
      {
        id: "coverage",
        header: t("loyalty.plans.coverage"),
        exportValue: (row) => [...row.coveredLineKinds, ...row.coveredProductIds].join(", "),
        cell: (row) => (
          <span className="line-clamp-2 max-w-72 text-sm">
            {[
              ...row.coveredLineKinds.map((kind) => POS_LINE_KIND_LABELS[kind]),
              ...row.coveredProductIds.map((id) => productNames.get(id) ?? id),
            ].join(", ")}
          </span>
        ),
      },
      {
        id: "entitlement",
        header: t("loyalty.plans.entitlement"),
        align: "right",
        exportValue: (row) => row.quantityPerPeriod,
        cell: (row) => (
          <span className="grid justify-items-end whitespace-nowrap">
            <span className="tabular">
              {t("loyalty.plans.perMonth", { quantity: formatEntitlement(row.quantityPerPeriod) })}
            </span>
            {row.dailyLimit ? (
              <span className="text-xs text-muted-foreground">
                {t("loyalty.plans.perDay", { quantity: formatEntitlement(row.dailyLimit) })}
              </span>
            ) : null}
            {row.vehicleBound ? (
              <span className="text-xs text-muted-foreground">{t("loyalty.plans.vehicleBound")}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "price",
        header: t("loyalty.plans.fields.price"),
        align: "right",
        exportValue: (row) => row.price,
        cell: (row) => <MoneyText value={row.price} />,
      },
      {
        id: "product",
        header: t("loyalty.plans.fields.product"),
        exportValue: (row) => (row.productId ? (productNames.get(row.productId) ?? row.productId) : null),
        cell: (row) =>
          row.productId ? (
            (productNames.get(row.productId) ?? row.productId)
          ) : (
            <span className="text-muted-foreground">{t("loyalty.plans.noProduct")}</span>
          ),
      },
      {
        id: "active",
        header: t("loyalty.plans.activeSubscriptions"),
        align: "right",
        exportValue: (row) => row.activeSubscriptions,
        cell: (row) => <span className="tabular">{formatCount(row.activeSubscriptions)}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
        cell: (row) => <ActiveBadge active={row.isActive} />,
      },
      {
        id: "actions",
        header: t("common.actions"),
        cell: (row) =>
          permissions.canManageSubscriptions ? (
            <Button variant="outline" size="sm" className="relative" onClick={() => onEdit(row)}>
              {t("common.edit")}
              <span className="sr-only"> — {row.code}</span>
            </Button>
          ) : null,
      },
    ],
    [t, productNames, permissions.canManageSubscriptions, onEdit],
  );

  return (
    <DataTable
      caption={t("loyalty.plans.caption")}
      columns={columns}
      rows={plans}
      rowKey={(row) => row.id}
      loading={loading}
      error={error ? <Alert tone="danger">{errorMessage(error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<CalendarSync />}
          title={t("loyalty.plans.emptyTitle")}
          description={t("loyalty.plans.emptyDescription")}
        />
      }
    />
  );
}
